import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DocumentsPanel from "./DocumentsPanel";
import InterviewPrepPanel from "./InterviewPrepPanel";

const STATUS_OPTIONS = ["Applied", "Interviewing", "Offer", "Rejected"];
const INTERVIEW_STAGES = ["Phone Screen", "Recruiter", "Technical", "Onsite", "Final"];

const empty = {
  company: "",
  job_title: "",
  website: "",
  job_url: "",
  pay_range: "",
  status: "Applied",
  submitted: false,
  date_applied: new Date().toISOString().slice(0, 10),
  interview_date: "",
  interview_stage: "",
  recruiter_name: "",
  recruiter_email: "",
  recruiter_linkedin: "",
  hiring_manager: "",
  last_contacted: "",
  notes: "",
};

export default function ApplicationModal({ open, onOpenChange, editing, prefill, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({ ...empty, ...editing });
    } else if (prefill) {
      setForm({ ...empty, ...prefill });
    } else {
      setForm(empty);
    }
     
  }, [editing, prefill, open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.company.trim() || !form.job_title.trim()) {
      toast.error("Company and job title are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/applications/${editing.id}`, form);
        toast.success("Application updated");
      } else {
        await api.post("/applications", form);
        toast.success("Application added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const dateValue = form.date_applied ? parseISO(form.date_applied) : undefined;
  const interviewValue = form.interview_date ? parseISO(form.interview_date) : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#FDFCFA] max-h-[90vh] overflow-y-auto" data-testid="application-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A362D]">
            {editing ? "Edit application" : "New application"}
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            {editing ? "Update the details below." : "Log the essentials — you can always refine later."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Company *</Label>
            <Input value={form.company} onChange={(e) => setField("company", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-company-input" />
          </div>
          <div>
            <Label>Job title *</Label>
            <Input value={form.job_title} onChange={(e) => setField("job_title", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-title-input" />
          </div>
          <div>
            <Label>Website / Source</Label>
            <Input placeholder="LinkedIn, Indeed, referral…"
              value={form.website} onChange={(e) => setField("website", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-website-input" />
          </div>
          <div>
            <Label>Job URL</Label>
            <Input placeholder="https://…"
              value={form.job_url} onChange={(e) => setField("job_url", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-url-input" />
          </div>
          <div>
            <Label>Pay / Pay range</Label>
            <Input placeholder="$120k – $145k"
              value={form.pay_range} onChange={(e) => setField("pay_range", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-pay-input" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger className="mt-1.5 bg-white h-11" data-testid="app-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`app-status-option-${s.toLowerCase()}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date applied</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="mt-1.5 h-11 w-full justify-start bg-white font-normal"
                  data-testid="app-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateValue ? format(dateValue, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={dateValue}
                  onSelect={(d) => d && setField("date_applied", d.toISOString().slice(0, 10))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[#E5E1DB] bg-white px-4 h-11 mt-6">
            <Label className="font-body">Application submitted</Label>
            <Switch checked={form.submitted} onCheckedChange={(v) => setField("submitted", v)}
              data-testid="app-submitted-switch" />
          </div>
          <div className="md:col-span-2">
            <Label>Interview date (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="mt-1.5 h-11 w-full justify-start bg-white font-normal"
                  data-testid="app-interview-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {interviewValue ? format(interviewValue, "PPP") : "No interview scheduled"}
                  {form.interview_date && (
                    <span
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setField("interview_date", ""); }}
                      className="ml-auto text-[#C65D47] text-xs underline"
                    >
                      clear
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={interviewValue}
                  onSelect={(d) => d && setField("interview_date", d.toISOString().slice(0, 10))}
                />
              </PopoverContent>
            </Popover>
          </div>

          {form.status === "Interviewing" && (
            <div className="md:col-span-2">
              <Label>Interview stage</Label>
              <Select value={form.interview_stage || "none"} onValueChange={(v) => setField("interview_stage", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5 bg-white h-11" data-testid="app-stage-select">
                  <SelectValue placeholder="Select interview stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not specified —</SelectItem>
                  {INTERVIEW_STAGES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`app-stage-option-${s.toLowerCase().replace(/ /g, '-')}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Contacts section */}
          <div className="md:col-span-2 mt-4 pt-4 border-t border-[#E5E1DB]">
            <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64] font-semibold">Contacts & follow-up</p>
          </div>
          <div>
            <Label>Recruiter name</Label>
            <Input value={form.recruiter_name} onChange={(e) => setField("recruiter_name", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-recruiter-name-input" />
          </div>
          <div>
            <Label>Recruiter email</Label>
            <Input type="email" placeholder="recruiter@company.com"
              value={form.recruiter_email} onChange={(e) => setField("recruiter_email", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-recruiter-email-input" />
          </div>
          <div>
            <Label>Recruiter LinkedIn</Label>
            <Input placeholder="https://linkedin.com/in/…"
              value={form.recruiter_linkedin} onChange={(e) => setField("recruiter_linkedin", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-recruiter-linkedin-input" />
          </div>
          <div>
            <Label>Hiring manager</Label>
            <Input value={form.hiring_manager} onChange={(e) => setField("hiring_manager", e.target.value)}
              className="mt-1.5 bg-white h-11" data-testid="app-hiring-manager-input" />
          </div>
          <div className="md:col-span-2">
            <Label>Last contacted</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="mt-1.5 h-11 w-full justify-start bg-white font-normal"
                  data-testid="app-last-contacted-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.last_contacted ? format(parseISO(form.last_contacted), "PPP") : "Never contacted"}
                  {form.last_contacted && (
                    <span
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setField("last_contacted", ""); }}
                      className="ml-auto text-[#C65D47] text-xs underline"
                    >
                      clear
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={form.last_contacted ? parseISO(form.last_contacted) : undefined}
                  onSelect={(d) => d && setField("last_contacted", d.toISOString().slice(0, 10))}
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-[#5C6B64] mt-1.5">
              Trackr nudges you when it's been 7+ days since your last contact.
            </p>
          </div>

          <div className="md:col-span-2 mt-4 pt-4 border-t border-[#E5E1DB]">
            <Label>Documents</Label>
            <div className="mt-1.5">
              <DocumentsPanel applicationId={editing?.id} />
            </div>
          </div>

          {editing?.id && (form.status === "Interviewing" || form.interview_date) && (
            <div className="md:col-span-2 mt-4 pt-4 border-t border-[#E5E1DB]">
              <Label>Interview prep</Label>
              <p className="text-xs text-[#5C6B64] mt-0.5 mb-2">
                5 likely questions + STAR starter answers, personalized to this role.
              </p>
              <InterviewPrepPanel applicationId={editing.id} status={form.status} />
            </div>
          )}

          <div className="md:col-span-2 mt-4 pt-4 border-t border-[#E5E1DB]">
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)}
              className="mt-1.5 bg-white" data-testid="app-notes-input" />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}
            className="rounded-full" data-testid="app-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving}
            className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-6"
            data-testid="app-save-btn">
            {saving ? "Saving…" : editing ? "Save changes" : "Add application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
